/**
 * Build-time and runtime configuration read from Vite env vars.
 *
 * Required:
 *   VITE_SUPABASE_URL          — the project URL (https://<ref>.supabase.co)
 *   VITE_SUPABASE_ANON_KEY     — the anon publishable key (used for unauthenticated PostgREST calls)
 *
 * Optional:
 *   VITE_AUTH_BRIDGE_URL       — override (default: VITE_SUPABASE_URL + /functions/v1/auth-bridge)
 *   VITE_ALLOWED_HOST_ORIGINS  — comma-separated list of postMessage origins allowed to hand us a Vario token
 */
export interface AppConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  authBridgeUrl: string;
  allowedHostOrigins: string[];
}

function required(name: string, value: string | undefined): string {
  if (!value) throw new Error(`Missing required env var ${name}`);
  return value;
}

export function loadConfig(): AppConfig {
  const supabaseUrl = required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL);
  const supabaseAnonKey = required('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY);
  // Use `||` not `??` — Vite/Dockerfile ARGs without a value get baked in as empty strings
  // (`""`), which `??` happily keeps; `||` falls through on every falsy value.
  const authBridgeUrl =
    import.meta.env.VITE_AUTH_BRIDGE_URL || `${supabaseUrl.replace(/\/$/, '')}/functions/v1/auth-bridge`;
  const allowedHostOrigins = (import.meta.env.VITE_ALLOWED_HOST_ORIGINS || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return { supabaseUrl, supabaseAnonKey, authBridgeUrl, allowedHostOrigins };
}
